import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux"; // useSelector is key here
import {
    ChatroommessagesAddWithChatroomID,
    AImessagesAddWithAIcallID,
    ConversationmessagesAddWithConversationID,
} from "./store"; // Make sure this path is correct

const WebSocketHandler = () => {
    const ws = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [shouldReconnect, setShouldReconnect] = useState(false);
    
    const dispatch = useDispatch();
    
    // Get token and agentInfo directly from the Redux store
    const authTokenFromRedux = useSelector(state => state.auth.token);
    const agentInfo = useSelector(state => state.auth.agentInfo);

    const maxRetries = 10;
    const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL || "wss://api.voipbin.net/v1.0/ws";

    // Log current state when component renders or critical state from Redux changes
    useEffect(() => {
        console.log("WS: Handler Update. authToken (Redux):", authTokenFromRedux ? "Present" : "Missing", "agentInfo (Redux):", agentInfo && agentInfo.id ? `Present (ID: ${agentInfo.id})` : "Missing/Incomplete");
    }, [authTokenFromRedux, agentInfo]);

    const topics = useMemo(() => {
        if (!agentInfo || !agentInfo.customer_id || !agentInfo.id) {
            return [];
        }
        return [
            `customer_id:${agentInfo.customer_id}:call`,
            `customer_id:${agentInfo.customer_id}:activeflow`,
            `customer_id:${agentInfo.customer_id}:aimessage`,
            `customer_id:${agentInfo.customer_id}:conversation`,
            `agent_id:${agentInfo.id}:messagechatroom`
        ];
    }, [agentInfo]);

    const handleMessage = useCallback((event) => {
        try {
            const parsedMessage = JSON.parse(event.data);
            const { type, data: messageData } = parsedMessage;
            console.log("WS: Received message:", type, messageData);

            switch (type) {
                case "messagechatroom_created":
                    dispatch(ChatroommessagesAddWithChatroomID(messageData.chatroom_id, messageData));
                    break;
                case "aimessage_created":
                    dispatch(AImessagesAddWithAIcallID(messageData.aicall_id, messageData));
                    break;
                case "conversation_message_created":
                    dispatch(ConversationmessagesAddWithConversationID(messageData.conversation_id, messageData));
                    break;
                default:
                    console.warn("WS: Unhandled WebSocket message type:", type, messageData);
                    break;
            }
        } catch (error) {
            console.error("WS: Error parsing WebSocket message:", error);
        }
    }, [dispatch]);

    const disconnectWebSocket = useCallback((reason = "No reason specified") => {
        setShouldReconnect(false);

        if (ws.current) {
            console.log(`WS: Disconnecting WebSocket deliberately. Reason: ${reason}`);
            ws.current.onopen = null;
            ws.current.onmessage = null;
            ws.current.onerror = null;
            ws.current.onclose = null;

            if (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING) {
                ws.current.close(1000, `Client initiated disconnect: ${reason}`);
            }
            ws.current = null;
            setIsConnected(false);
        }
    }, [setIsConnected, setShouldReconnect]);

    const connectWebSocket = useCallback(() => {
        console.log("WS: connectWebSocket function invoked.");

        // Use authTokenFromRedux and agentInfo from Redux
        if (!authTokenFromRedux || !agentInfo || !agentInfo.customer_id || !agentInfo.id) {
            console.log("WS: connectWebSocket: Preconditions (authTokenFromRedux/agentInfo) NOT met. Aborting connection attempt.", {
                hasAuthTokenFromRedux: !!authTokenFromRedux,
                hasAgentInfo: !!agentInfo,
                agentInfoId: agentInfo ? agentInfo.id : 'N/A'
            });
            return;
        }

        console.log("WS: connectWebSocket: Preconditions met. Proceeding with connection.");

        if (ws.current) {
            console.log("WS: connectWebSocket: Closing existing WebSocket before creating a new one.");
            ws.current.onopen = null;
            ws.current.onmessage = null;
            ws.current.onerror = null;
            ws.current.onclose = null;
            ws.current.close(1000, "Re-establishing connection by connectWebSocket");
            ws.current = null;
            setIsConnected(false);
        }

        // Use authTokenFromRedux from Redux state
        const url = `${websocketUrl}?token=${authTokenFromRedux}`;
        console.log("WS: connectWebSocket: Attempting to connect to WebSocket URL:", url);
        console.log("WS: connectWebSocket: Using topics:", topics);


        let localWsInstance;
        try {
            localWsInstance = new WebSocket(url);
        } catch (error) {
            console.error("WS: CRITICAL - Failed to create WebSocket object:", error);
            setIsConnected(false);
            // Check authTokenFromRedux and agentInfo before deciding to retry
            if (authTokenFromRedux && agentInfo) {
                setRetryCount(prev => prev + 1);
                setShouldReconnect(true);
            }
            return;
        }
        
        ws.current = localWsInstance;

        localWsInstance.onopen = () => {
            console.log("WS: >>> WebSocket connected successfully! <<<");
            setIsConnected(true);
            setRetryCount(0);
            setShouldReconnect(false);
            if (localWsInstance.readyState === WebSocket.OPEN) {
                if (topics && topics.length > 0) {
                    console.log("WS: Subscribing to topics:", topics);
                    localWsInstance.send(JSON.stringify({ type: "subscribe", topics }));
                } else {
                    console.warn("WS: Connected, but no topics to subscribe to. Check agentInfo and topics logic.", agentInfo);
                }
            }
        };

        localWsInstance.onmessage = handleMessage;

        localWsInstance.onclose = (event) => {
            console.log(`WS: WebSocket disconnected. Code: ${event.code}, Reason: "${event.reason}", Clean: ${event.wasClean}`);
            if (ws.current === localWsInstance || !ws.current) {
                setIsConnected(false);
                ws.current = null;
                // Check authTokenFromRedux and agentInfo before deciding to retry
                if (authTokenFromRedux && agentInfo && event.code !== 1000) {
                    console.log("WS: Flagging for potential reconnect due to unclean disconnect (onclose).");
                    setShouldReconnect(true);
                }
            } else {
                console.log("WS: onclose event for an old WebSocket instance. Ignoring for reconnect logic.");
            }
        };

        localWsInstance.onerror = (errorEvent) => {
            console.error("WS: WebSocket error observed:", errorEvent);
            if (ws.current === localWsInstance || !ws.current) {
                setIsConnected(false);
                if (localWsInstance.readyState !== WebSocket.CLOSED && localWsInstance.readyState !== WebSocket.CLOSING) {
                    localWsInstance.close();
                } else {
                     ws.current = null;
                }
                // Check authTokenFromRedux and agentInfo before deciding to retry
                if (authTokenFromRedux && agentInfo) {
                    console.log("WS: Flagging for potential reconnect due to WebSocket error (onerror).");
                    setShouldReconnect(true);
                }
            } else {
                 console.log("WS: onerror event for an old WebSocket instance. Ignoring.");
            }
        };
    }, [
        websocketUrl,
        authTokenFromRedux, // Now depends on authTokenFromRedux from Redux state
        agentInfo,
        topics,
        handleMessage,
        setIsConnected, setRetryCount, setShouldReconnect
    ]);

    // Effect to manage WebSocket connection based on authTokenFromRedux and agentInfo from Redux
    useEffect(() => {
        console.log("WS: Main connection effect RUNNING. authToken (Redux):", authTokenFromRedux ? "PRESENT" : "MISSING", "agentInfo (Redux):", agentInfo && agentInfo.id ? `PRESENT (ID: ${agentInfo.id})` : "MISSING/INCOMPLETE");
        
        // Use authTokenFromRedux from Redux state
        if (authTokenFromRedux && agentInfo && agentInfo.customer_id && agentInfo.id) {
            console.log("WS: ✅ Main effect: Conditions MET. Calling connectWebSocket.");
            connectWebSocket();
        } else {
            console.log("WS: ❌ Main effect: Conditions NOT MET. Calling disconnectWebSocket.", {
                hasAuthTokenFromRedux: !!authTokenFromRedux,
                hasAgentInfo: !!agentInfo,
                hasAgentCustomerId: !!(agentInfo && agentInfo.customer_id),
                hasAgentId: !!(agentInfo && agentInfo.id)
            });
            disconnectWebSocket("AuthToken from Redux or complete AgentInfo missing in main effect");
        }

        return () => {
            console.log("WS: Main effect CLEANUP. Disconnecting WebSocket.");
            disconnectWebSocket("Main effect cleanup (unmount or deps change)");
        };
    }, [authTokenFromRedux, agentInfo, connectWebSocket, disconnectWebSocket]); // Depends on Redux state

    // Effect to handle automatic reconnection
    useEffect(() => {
        let timerId;
        // Use authTokenFromRedux from Redux state
        if (shouldReconnect && authTokenFromRedux && agentInfo && agentInfo.customer_id && agentInfo.id) {
            if (retryCount < maxRetries) {
                const delay = 3000 * Math.pow(2, Math.min(retryCount, 3));
                console.log(`WS: Reconnect effect: Attempt ${retryCount + 1}/${maxRetries} scheduled in ${delay / 1000}s.`);
                
                timerId = setTimeout(() => {
                    console.log("WS: Reconnect effect: Executing scheduled reconnection attempt.");
                    setRetryCount(prevCount => prevCount + 1);
                    setShouldReconnect(false);
                    connectWebSocket();
                }, delay);
            } else {
                console.log("WS: Reconnect effect: Max retries reached. Not attempting further reconnections.");
                setShouldReconnect(false);
            }
        } else if (shouldReconnect) {
            console.log("WS: Reconnect effect: 'shouldReconnect' is true, but other conditions (authTokenFromRedux, agentInfo, retries) not met. Resetting.", {
                hasAuthTokenFromRedux: !!authTokenFromRedux,
                hasAgentInfo: !!(agentInfo && agentInfo.id),
                currentRetryCount: retryCount
            });
            setShouldReconnect(false);
        }

        return () => {
            clearTimeout(timerId);
        };
    }, [shouldReconnect, retryCount, maxRetries, authTokenFromRedux, agentInfo, connectWebSocket, setRetryCount, setShouldReconnect]); // Depends on Redux state

    return (
        <div style={{ display: 'none' }} data-testid="websocket-handler">
            {/* Debug info can be uncommented if needed */}
            {/* <p>WS Status: {isConnected ? "Connected" : "Disconnected"}</p>
            {authTokenFromRedux ? null : <p style={{color: 'red'}}>AuthToken (Redux) Missing</p>}
            {agentInfo && agentInfo.id ? null : <p style={{color: 'red'}}>AgentInfo Missing/Incomplete</p>}
            {shouldReconnect && retryCount < maxRetries && <p>Reconnecting... (Attempt: {retryCount +1})</p>} */}
        </div>
    );
};

export default WebSocketHandler;